import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addByIncrement } from "../../store/reducers/cartSlice";
import useFetch from "../../utils/useFetch";
import { formatNPR, formatNPRRange } from '../../utils/currency';
import "./product.css";
import Card from "../card/Card";
import { getMoqFromInventory, normalizeOrderQuantity } from "../../utils/orderQuantity";
import {
  autoCompleteSelection,
  findMatchingInventories,
  formatVariantSummary,
  getAttributeKeys,
  buildGalleryImageInventoryMap,
  getAllProductGalleryImages,
  getGalleryIndicesForSelection,
  getInitialSelection,
  getSelectionForGalleryIndex,
  getVariantLabelForGalleryIndex,
  isSelectionComplete,
} from "../../utils/productVariants";
import WholesalePurchaseSelector from "../orderQuantity/WholesalePurchaseSelector";
import ProductVariantPicker from "./ProductVariantPicker";
import ProductGalleryPremium from "./ProductGalleryPremium";
import ProductDetailsPanel from "./ProductDetailsPanel";

function Product() {

  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [filteredInventory, setFilteredInventory] = useState(null);
  const [availableStock, setAvailableStock] = useState(0);

  const [isExpanded, setIsExpanded] = useState(false);
  const [gallerySyncToken, setGallerySyncToken] = useState(0);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [total, setTotal] = useState(
    filteredInventory?.[0]?.properties?.product_info?.minimum_order_quantity || 0
  );
  const { productTitle } = useParams();
  const navigate = useNavigate();
  const Api = `inventory/products`;
  const dispatch = useDispatch();

  // Get cart items from Redux store
  const cartItems = useSelector((state) => state.cart.cartItems);

  const { data, loading, error } = useFetch(`${Api}/${productTitle}/`);

  const attributeKeys = data?.inventories_details
    ? getAttributeKeys(data.inventories_details, data.attributes)
    : [];

  const selectedInventory =
    filteredInventory?.length > 0 ? filteredInventory[0] : null;

  const variantSummary = formatVariantSummary(selectedInventory, data);

  const RelatedProductApi = `inventory/products/?category=${data?.category_details?.slug}`;

  const {
    data: RelatedProduct,
    loading: RelatedProductLoading,
    error: RelatedProductError
  } = useFetch(`${RelatedProductApi}`);

  useEffect(() => {
    if (data?.inventories_details?.length > 0) {
      const keys = getAttributeKeys(data.inventories_details, data.attributes);
      const initial = autoCompleteSelection(
        data.inventories_details,
        getInitialSelection(data.inventories_details),
        keys
      );
      setSelectedAttributes(initial);
      setGallerySyncToken((t) => t + 1);
    }
  }, [data]);

  useEffect(() => {
    if (filteredInventory?.[0]) {
      const moq = getMoqFromInventory(filteredInventory[0], data);
      setTotal(moq);
    }
  }, [filteredInventory, data]);

  useEffect(() => {
    if (!loading && data) {
      window.scrollTo(0, 0);
    }
  }, [loading, data]);

  useEffect(() => {
    if (data?.inventories_details) {
      const keys = getAttributeKeys(data.inventories_details, data.attributes);
      if (isSelectionComplete(selectedAttributes, keys)) {
        const filtered = findMatchingInventories(
          data.inventories_details,
          selectedAttributes
        );
        setFilteredInventory(filtered.length > 0 ? filtered : null);
      } else {
        setFilteredInventory(null);
      }
    }
  }, [selectedAttributes, data]);

  useEffect(() => {
    if (filteredInventory?.length > 0) {
      const currentItem = filteredInventory[0];
      const itemInCart = cartItems.find(item => item.id === currentItem.id);
      const cartQuantity = itemInCart?.cartQuantity || 0;
      setAvailableStock(currentItem.stock - cartQuantity);
    }
  }, [filteredInventory, cartItems]);

  const images = getAllProductGalleryImages(data, data?.inventories_details);

  const galleryImageMap = useMemo(
    () => buildGalleryImageInventoryMap(data, data?.inventories_details),
    [data]
  );

  const handleAttributeChange = (nextSelection) => {
    setSelectedAttributes(nextSelection);
    setGallerySyncToken((t) => t + 1);
    const { focusIndex } = getGalleryIndicesForSelection(
      images,
      data?.inventories_details,
      nextSelection
    );
    setActiveGalleryIndex(focusIndex);
  };

  const { focusIndex: galleryFocusIndex } = getGalleryIndicesForSelection(
    images,
    data?.inventories_details,
    selectedAttributes
  );

  const activeVariantLabel =
    galleryImageMap[activeGalleryIndex]?.label ||
    getVariantLabelForGalleryIndex(
      images,
      data?.inventories_details,
      activeGalleryIndex,
      data
    );

  const handleGalleryImageChange = useCallback(
    (index) => {
      setActiveGalleryIndex(index);
      const slide = galleryImageMap[index];
      if (slide?.selection) {
        setSelectedAttributes({ ...slide.selection });
        return;
      }
      const next = getSelectionForGalleryIndex(
        images,
        data?.inventories_details,
        index,
        data
      );
      if (next) setSelectedAttributes({ ...next });
    },
    [galleryImageMap, images, data]
  );

  const addSelectedToCart = (inventory, { redirectToCheckout = false } = {}) => {
    if (!inventory?.length) return;
    const product = inventory[0];
    const moq = getMoqFromInventory(product, data);
    const maxQty = availableStock;
    const quantity = normalizeOrderQuantity(total, {
      step: moq,
      min: moq,
      max: maxQty,
    });
    setTotal(quantity);
    dispatch(addByIncrement({ product, cartQuantity: quantity }));
    if (redirectToCheckout) {
      navigate("/checkout");
    }
  };

  const handleAddToCart = (inventory) => addSelectedToCart(inventory);
  const handleBuyNow = (inventory) =>
    addSelectedToCart(inventory, { redirectToCheckout: true });

  const unitPrice = selectedInventory
    ? parseFloat(selectedInventory.price)
    : null;

  const renderPrice = () => {
    const priceSource =
      filteredInventory?.length > 0
        ? filteredInventory
        : findMatchingInventories(data?.inventories_details || [], selectedAttributes);

    if (priceSource.length > 0) {
      if (priceSource.length > 1) {
        const prices = priceSource.map((item) => parseFloat(item?.price));
        return formatNPRRange(Math.min(...prices), Math.max(...prices));
      }
      return formatNPR(priceSource[0].price);
    }
    const low = data?.properties?.price_range?.lowest_price;
    const high = data?.properties?.price_range?.highest_price;
    return low != null && high != null ? formatNPRRange(low, high) : "—";
  };

  const renderStockBadge = () => {
    if (!selectedInventory) {
      return (
        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
          Select options
        </span>
      );
    }
    if (availableStock > 0) {
      const lowStock = availableStock < 50;
      return (
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            lowStock && availableStock < 20
              ? "bg-amber-100 text-amber-800"
              : "bg-brand-100 text-brand-800"
          }`}
        >
          {lowStock ? `${availableStock} in stock` : "In stock"}
        </span>
      );
    }
    return (
      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700">
        Out of stock
      </span>
    );
  };

  const renderRating = () => {
    const rating = data?.average_rating ?? data?.properties?.average_rating;
    const count = data?.review_count ?? data?.properties?.review_count;
    if (rating != null && count != null) {
      const filled = Math.round(parseFloat(rating));
      return (
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-amber-400 text-xs tracking-tight" aria-label={`${rating} out of 5`}>
            {"★".repeat(Math.min(5, filled))}
            {"☆".repeat(Math.max(0, 5 - filled))}
          </span>
          <span className="text-xs text-slate-500">({count} reviews)</span>
        </div>
      );
    }
    return (
      <span className="text-[10px] font-medium text-slate-500 mt-0.5 block">
        Wholesale · bundle pricing
      </span>
    );
  };

  return (
    <div className="bg-gray-50">
      {loading ? <div className="px-0 py-10 lg:py-10 animate-pulse">
        <div className="mx-auto px-3 lg:px-10 max-w-screen-2xl">
          {/* Breadcrumb Skeleton */}
          <div className="flex items-center pb-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          </div>

          <div className="w-full rounded-lg p-3 lg:p-12 bg-white">
            <div className="flex flex-col xl:flex-row gap-8">
              {/* Image Gallery Skeleton */}
              <div className="w-full md:w-6/12 lg:w-5-12 xl:w-4/12">
                <div className="w-full h-96 bg-gray-200 rounded-lg"></div>
              </div>

              {/* Product Info Skeleton */}
              <div className="w-full">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-full md:w-7/12">
                    {/* Title Skeleton */}
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>

                    {/* Price Skeleton */}
                    <div className="h-5 bg-gray-200 rounded w-1/4 mb-6"></div>

                    {/* Description Skeleton */}
                    <div className="space-y-2 mb-6">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 rounded w-4/5"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>

                    {/* Attributes Skeleton */}
                    <div className="space-y-4 mb-6">
                      {[...Array(3)].map((_, i) => (
                        <div key={i}>
                          <div className="h-3 bg-gray-200 rounded w-1/5 mb-2"></div>
                          <div className="flex gap-2">
                            {[...Array(4)].map((_, j) => (
                              <div key={j} className="h-8 bg-gray-200 rounded w-16"></div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Quantity Controls Skeleton */}
                    <div className="flex gap-4 mb-6">
                      <div className="h-12 bg-gray-200 rounded w-32"></div>
                      <div className="h-12 bg-gray-200 rounded w-full"></div>
                    </div>

                    {/* Category & Tags Skeleton */}
                    <div className="space-y-4">
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      <div className="flex gap-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="h-6 bg-gray-200 rounded w-16"></div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Information Sidebar Skeleton */}
                  <div className="w-full md:w-5/12">
                    <div className="h-96 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products Skeleton */}
          <div className="pt-10 llg:pt-20 lg:pb-10">
            <div className="h-5 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div> : (error || !data) ? (
        // Failed or missing product (deleted item, stale link): a friendly
        // dead-end instead of crashing on data.category_details (#61).
        <div className="mx-auto max-w-screen-2xl px-3 lg:px-10 py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            This product is no longer available
          </h2>
          <p className="text-gray-600 mb-8">
            It may have been removed, or the link is out of date.
          </p>
          <Link
            to="/search"
            className="!no-underline inline-flex items-center justify-center px-6 py-3 rounded-md bg-brand-600 hover:bg-brand-700 !text-white font-semibold transition-colors"
          >
            Browse products
          </Link>
        </div>
      ) : (
        <div className="px-0 py-4 sm:py-5 lg:py-6">
          <div className="mx-auto px-3 sm:px-4 lg:px-8 max-w-screen-xl">
            <nav className="flex items-center pb-2 overflow-x-auto scrollbar-hide" aria-label="Breadcrumb">
              <ol className="flex items-center text-sm whitespace-nowrap">
                <li className="text-sm pr-1 transition duration-200 ease-in cursor-pointer  font-semibold">
                  <Link
                    className="!no-underline !text-black hover:!text-brand-500"
                    to="/"
                  >
                    Home
                  </Link>
                </li>
                <li className="text-sm mt-[1px]">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </li>
                <li className="text-sm pr-1 transition duration-200 ease-in cursor-pointer hover:text-brand-600 font-semibold">
                  <Link
                    className="!no-underline !text-black hover:!text-brand-500"
                    to={`/search?category=${data.category_details.slug}`}
                  >
                    {data.category_details.name}
                  </Link>
                </li>
                <li className="text-sm mt-[1px]">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </li>
                <li className="text-sm px-1 transition capitalize duration-200 ease-in ">
                  {data?.name}
                </li>
              </ol>
            </nav>

            <article className="w-full rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-12">
                {/* Images — full bleed on mobile */}
                <div className="md:col-span-5 lg:col-span-5 min-w-0 px-2 pt-2 pb-1 sm:px-3 sm:pt-3 md:py-3 md:pr-2 md:border-r border-slate-100">
                  <ProductGalleryPremium
                    key={data?.id}
                    images={images}
                    focusIndex={galleryFocusIndex}
                    syncToken={gallerySyncToken}
                    activeVariantLabel={activeVariantLabel}
                    onActiveImageChange={handleGalleryImageChange}
                  />
                </div>

                {/* Buy box */}
                <div className="md:col-span-7 lg:col-span-7 flex flex-col min-w-0">
                  <div className="md:sticky md:top-14 lg:top-16 z-10 bg-white px-2 pb-2 pt-1 sm:px-3 sm:pb-3 sm:pt-2 space-y-2.5">
                    <header className="flex gap-3 items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <Link
                          className="text-[10px] font-medium text-brand-600 hover:text-brand-700"
                          to={`/search?Category=${data.category_details.slug}`}
                        >
                          {data.category_details.name}
                        </Link>
                        <h1 className="text-base sm:text-lg font-bold text-slate-900 capitalize leading-tight">
                          {data?.name}
                        </h1>
                        {renderRating()}
                      </div>
                      <div className="text-right shrink-0 pt-0.5">
                        <p className="text-base sm:text-lg font-bold text-slate-900 tabular-nums leading-none">
                          {renderPrice()}
                        </p>
                        <div className="mt-1">{renderStockBadge()}</div>
                      </div>
                    </header>

                    <ProductVariantPicker
                      compact
                      attributes={data?.attributes}
                      inventories={data?.inventories_details}
                      selectedAttributes={selectedAttributes}
                      onChange={handleAttributeChange}
                    />

                    {variantSummary && selectedInventory && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600 px-0.5 -mt-1">
                        <span>
                          Bundle MOQ:{' '}
                          <strong className="text-slate-800">
                            {getMoqFromInventory(selectedInventory, data)} pcs
                          </strong>
                        </span>
                        {variantSummary.stock != null && (
                          <span>
                            Stock:{' '}
                            <strong className="text-slate-800">{availableStock} pcs</strong>
                          </span>
                        )}
                      </div>
                    )}

                    {selectedInventory ? (
                      <WholesalePurchaseSelector
                        moq={getMoqFromInventory(selectedInventory, data)}
                        total={total}
                        onChange={setTotal}
                        maxQty={availableStock}
                        availableStock={availableStock}
                        unitPrice={unitPrice}
                        onAddToCart={() => handleAddToCart(filteredInventory)}
                        onBuyNow={() => handleBuyNow(filteredInventory)}
                      />
                    ) : (
                      <p className="text-xs text-center text-slate-500 py-3 border border-dashed border-slate-200 rounded-lg">
                        Choose variations above to order
                      </p>
                    )}
                  </div>

                  <ProductDetailsPanel
                    description={data?.description}
                    tags={data?.tags}
                    sku={selectedInventory?.sku}
                    isExpanded={isExpanded}
                    onToggleExpand={() => setIsExpanded(!isExpanded)}
                  />
                </div>
              </div>
            </article>
            {!RelatedProductLoading && (
              <div className="pt-6 lg:pt-8 lg:pb-6">
                <h3 className="text-base font-semibold text-slate-800 mb-2">
                  Related Products
                </h3>
                <div className="flex">
                  <div className="w-full">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-6 gap-2 md:gap-3 lg:gap-3 ">
                      {RelatedProduct?.results?.length > 0 ? (
                        RelatedProduct.results.map((data, index) => (
                          <Card key={index} data={data} />
                        ))
                      ) : (
                        <div className="col-span-full text-center py-4 text-gray-500">
                          No related products available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )};
    </div>
  );
}

export default Product;
